import { Component, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { extractACMEErrorMessage } from '@b2b/types/core';

import { AuthenticationService } from '../../core/auth/authentication.service';
import { RedirectService } from '../../core/navigation/redirect.service';
import { RouteParamsService } from '../../core/navigation/route-params.service';
import { ZendeskSsoService, ZendeskSsoSession } from './zendesk-sso.service';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** Which auth-failure pop-up (if any) is currently showing. */
export type AuthErrorPopupKind = 'forbidden' | 'unauthorized';

interface AuthErrorPopupCopy {
  title: string;
  body: string;
  buttonText: string;
}

// DEV-11863 (AC1/AC2): the SSO handoff returns 403 (authenticated, no
// Zendesk/Helpdesk access) or 401 (unauthenticated / bad credentials / expired
// session). Both surface an in-page, dismissible pop-up instead of a silent
// redirect to an externally-hosted Zendesk error page.
const AUTH_ERROR_POPUP_COPY: Record<AuthErrorPopupKind, AuthErrorPopupCopy> = {
  forbidden: {
    title: "You don't have Helpdesk access",
    body: "Your account doesn't currently have access to the Help Center. Please contact your Tenant Administrator to request access.",
    buttonText: 'Close',
  },
  unauthorized: {
    title: 'Incorrect Sign-In',
    body: 'Incorrect username or password.  Please sign in again.',
    buttonText: 'Try Again',
  },
};

/**
 * Flow state for the Zendesk SSO login page.
 *  - `checking`   — optimistic seamless attempt in flight (AC3).
 *  - `form`       — show the credential form (no seamless session).
 *  - `submitting` — credential submission in flight.
 */
export type ZendeskLoginMode = 'checking' | 'form' | 'submitting';

/**
 * Zendesk SSO login page (DEV-11861).
 *
 * On load it optimistically attempts a session using the existing b2b cookie;
 * if Rails returns a redirect URL the browser is sent straight to Zendesk
 * (seamless SSO, AC3). Otherwise it shows a reduced credential form — modelled
 * on the standard login page but without the forgot-password link, the SSO
 * button, or the LDAP/tenant branching — and submits the credentials to the
 * same SSO session endpoint (AC2).
 *
 * Field values use Reactive Forms; flow state is held in a signal; the final
 * redirect goes through the injectable {@link RedirectService} seam.
 */
@Component({
  selector: 'app-zendesk-login',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="zendesk-login">
      @if (mode() === 'checking') {
        <div class="zendesk-login-form col-sm-5">
          <div class="zendesk-login-panel panel panel-default panel-inverted">
            <h3 class="zendesk-login-panel-header">One moment please&hellip;</h3>
          </div>
        </div>
      } @else {
        <form
          role="form"
          class="zendesk-login-form col-sm-5"
          [formGroup]="form"
          (ngSubmit)="submit()"
        >
          <div class="zendesk-login-panel panel panel-default panel-inverted">
            <h3 class="zendesk-login-panel-header">
              @if (mode() === 'submitting') {
                <span>One moment please&hellip;</span>
              } @else {
                <span>Sign into your account</span>
              }
            </h3>

            <div class="zendesk-login-panel-body panel-body">
              @if (errorMessage()) {
                <div class="alert alert-danger" role="alert">{{ errorMessage() }}</div>
              }
              <div class="form-group">
                <input
                  type="email"
                  name="email"
                  class="form-control"
                  formControlName="email"
                  placeholder="Email Address"
                  aria-label="Email Address"
                />
              </div>
              <div class="form-group">
                <input
                  type="password"
                  name="password"
                  class="form-control"
                  formControlName="password"
                  placeholder="Password"
                  aria-label="Password"
                />
              </div>
            </div>

            <div class="panel-footer">
              <button
                type="submit"
                class="btn btn-default btn-primary zendesk-login-signin"
                [disabled]="!canSubmit()"
              >
                Sign In
              </button>
            </div>
          </div>
        </form>
      }

      @if (authErrorPopup(); as popup) {
        <div class="modal-backdrop fade in"></div>
        <div
          class="modal fade in"
          style="display: block;"
          role="dialog"
          aria-modal="true"
          aria-labelledby="zendesk-login-popup-title"
          tabindex="-1"
        >
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h3 class="modal-title" id="zendesk-login-popup-title">{{ popupCopy[popup].title }}</h3>
              </div>
              <div class="modal-body">
                <p>{{ popupCopy[popup].body }}</p>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-default btn-primary" (click)="dismissPopup()">
                  {{ popupCopy[popup].buttonText }}
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class ZendeskLoginComponent implements OnInit {
  private readonly sso = inject(ZendeskSsoService);
  private readonly redirect = inject(RedirectService);
  private readonly auth = inject(AuthenticationService);
  private readonly routeParams = inject(RouteParamsService);

  readonly mode = signal<ZendeskLoginMode>('checking');
  readonly errorMessage = signal<string | null>(null);
  readonly authErrorPopup = signal<AuthErrorPopupKind | null>(null);
  readonly popupCopy = AUTH_ERROR_POPUP_COPY;

  readonly form = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  ngOnInit(): void {
    void this.loadSession();
  }

  /** Submit is allowed only with a valid form and no request already in flight. */
  canSubmit(): boolean {
    return this.form.valid && this.mode() !== 'submitting';
  }

  /** Dismiss whichever auth-error pop-up is showing and return to the credential form. */
  dismissPopup(): void {
    this.authErrorPopup.set(null);
    this.mode.set('form');
  }

  /**
   * AC3: optimistically attempt a Zendesk session using the existing session
   * cookie. Success → redirect to the client-assembled URL. A 403 (no Zendesk
   * access), a 401 (unauthenticated), or a success with no JWT (also "no
   * access") show the corresponding pop-up instead of navigating away
   * (DEV-11863). Any other failure (e.g. no valid cookie) → credential form.
   */
  async loadSession(): Promise<void> {
    try {
      // The SSO session POST is CSRF-protected; the new Angular CsrfInterceptor
      // only attaches the token once it has been loaded, which nothing does on
      // this landing route. Fetch it before the seamless attempt or Rails
      // rejects the request with 422 (InvalidAuthenticityToken).
      await this.auth.updateCSRF();
      const response = await this.sso.createSession(this.routeParams.get('return_to'));
      const url = this.buildRedirectUrl(response.data?.session);
      if (url) {
        this.redirect.to(url);
        return;
      }
      // AC1/AC2: authenticated via cookie but no JWT issued → not authorized.
      this.authErrorPopup.set('forbidden');
    } catch (error) {
      const status = (error as { status?: number } | null)?.status;
      if (status === 403) {
        this.authErrorPopup.set('forbidden');
        return;
      }
      if (status === 401) {
        this.authErrorPopup.set('unauthorized');
        return;
      }
      this.mode.set('form');
    }
  }

  /**
   * AC2: submit the entered credentials to the SSO session endpoint and, on
   * success, redirect to the client-assembled Zendesk URL.
   */
  async submit(): Promise<void> {
    if (!this.canSubmit()) {
      return;
    }

    this.errorMessage.set(null);
    this.authErrorPopup.set(null);
    this.mode.set('submitting');
    try {
      // Ensure the CSRF token is loaded before the (mutating) submit POST, in
      // case the seamless attempt's fetch did not run or failed. See loadSession.
      await this.auth.updateCSRF();
      const response = await this.sso.createSession(
        this.routeParams.get('return_to'),
        this.form.getRawValue(),
      );
      const url = this.buildRedirectUrl(response.data?.session);
      if (url) {
        this.redirect.to(url);
        return;
      }
      // AC1/AC2: a success with no jwt means no Zendesk access was granted.
      this.authErrorPopup.set('forbidden');
    } catch (error) {
      const status = (error as { status?: number } | null)?.status;
      if (status === 403) {
        this.authErrorPopup.set('forbidden');
        return;
      }
      if (status === 401) {
        this.authErrorPopup.set('unauthorized');
        return;
      }
      // AC4: other failures stay on the form. Rails owns the customer-facing
      // copy in the standard wrapped ACME error shape; fall back to a generic
      // line if absent.
      const body = (error as { data?: unknown } | null)?.data;
      this.errorMessage.set(extractACMEErrorMessage(body) || GENERIC_ERROR);
      this.mode.set('form');
    }
  }

  /**
   * Assemble the Zendesk access URL from the session in the Rails envelope.
   * Returns `null` when either field is missing/empty so callers can treat it
   * as a failure instead of navigating to a malformed URL.
   */
  private buildRedirectUrl(session: Partial<ZendeskSsoSession> | undefined): string | null {
    const returnTo = session?.returnTo?.trim();
    const jwt = session?.jwt?.trim();
    if (!returnTo || !jwt) {
      return null;
    }
    return `https://peek.zendesk.com/access/jwt?jwt=${jwt}&return_to=${returnTo}`;
  }
}
